import {
    Upload,
    ViewModule,
    ViewList,
    Search,
    Clear,
    Error as ErrorIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Typography,
    Button,
    CircularProgress,
    Tabs,
    Tab,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    Stack,
    TextField,
    InputAdornment,
    Paper,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DocumentCard from '../components/DocumentCard';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import TicketsAlert from '../components/TicketsAlert';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import DocumentPreview from 'components/v2/common/documentPreview';
import Loader from 'components/v2/common/loader';
import PageHeader from 'components/v2/common/pageHeader';
import Result from 'components/v2/result';
import PageBackground from 'components/v2/PageBackground';
import NoApplicationScreen from 'views/v2/applicant/noApplication/components/noApplicationScreen';

import api, { AppDocStatus } from 'api';
import { DocumentStatus } from 'constant';
import useDocuments from 'hooks/useDocuments';
import useModalObj from 'hooks/useModalObj';
import useTickets from 'hooks/useTickets';
import { setAlert } from 'redux/alert';
import { loadDocs, deleteDocs } from 'redux/application/documents/documents-middleware';
import { AppDispatch, RootState } from 'redux/store';
import type { ApplicationDocumentInterface, TicketInterface } from 'types';
import { getExplanationUrl } from 'utils/v2/document';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { toTitleCase } from 'utils/v2/text';

/* ── Document / Template / Sample preview wrappers ── */
interface PreviewModalBaseProps {
    documentId: number;
    documentName: string;
    mobile: boolean;
    onClose: () => void;
}

interface ApplicationPreviewModalProps extends PreviewModalBaseProps {
    applicationId: string;
}

const DocumentPreviewModal = ({
    documentId,
    documentName,
    mobile,
    onClose,
}: PreviewModalBaseProps) => {
    const { getDocumentPreviewUrl, isFetchingDocumentPreviewUrl } = useDocuments();
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        getDocumentPreviewUrl(String(documentId)).then((url) => {
            if (!cancelled && url) setFileUrl(url);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId]);

    return (
        <DocumentPreview
            open
            mobile={mobile}
            fileName={documentName}
            fileUrl={isFetchingDocumentPreviewUrl ? null : fileUrl}
            title="Document Preview"
            onClose={onClose}
        />
    );
};

const TemplatePreviewModal = ({
    applicationId,
    documentId,
    documentName,
    mobile,
    onClose,
}: ApplicationPreviewModalProps) => {
    const { getDocumentTemplatePreviewUrl, isFetchingTemplatePreviewUrl } = useDocuments();
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        getDocumentTemplatePreviewUrl(applicationId, documentId).then((url) => {
            if (!cancelled && url) setFileUrl(url);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, documentId]);

    return (
        <DocumentPreview
            open
            mobile={mobile}
            fileName={`${documentName} Template`}
            fileUrl={isFetchingTemplatePreviewUrl ? null : fileUrl}
            title="Template Preview"
            onClose={onClose}
        />
    );
};

const SamplePreviewModal = ({
    applicationId,
    documentId,
    documentName,
    mobile,
    onClose,
}: ApplicationPreviewModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        api.application
            .getDocumentSampleUrl(applicationId, documentId)
            .then((response) => {
                if (!cancelled) setFileUrl(response.data.url);
            })
            .catch((error) => {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(error, 'Failed to load sample preview'),
                            type: 'error',
                        })
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [applicationId, documentId, dispatch]);

    return (
        <DocumentPreview
            open
            mobile={mobile}
            fileName={`${documentName} Sample`}
            fileUrl={isLoading ? null : fileUrl}
            title="Sample Preview"
            onClose={onClose}
        />
    );
};

const DocumentsPage = () => {
    const dispatch: AppDispatch = useDispatch();

    const params = useParams();
    const selectedApplicationId = Number(params.id);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const user = useSelector((state: RootState) => state.user.entity);

    const {
        list: documents,
        isLoading: isLoadingDocuments,
        error: documentsError,
    } = useSelector((state: RootState) => state.documentsApp);

    const {
        applications,
        loading,
        error: applicationsError,
    } = useSelector((state: RootState) => state.applications);
    const { handleDownloadDoc, isDownloading, handleUploadDoc, isUploading } = useDocuments();
    const { tickets, fetchTickets, resolveTicket } = useTickets();

    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [viewMode, setViewMode] = useState<'grid' | 'row'>('grid');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [resolvingIds, setResolvingIds] = useState<Set<number>>(new Set());

    const { modalObject, onResetModalObj, setModalObject } = useModalObj<
        ApplicationDocumentInterface | TicketInterface | string | null
    >();

    const isLoadingApplications = loading === 'pending';
    const selectedApplication = applications.find((app) => app.id === selectedApplicationId);

    useEffect(() => {
        if (selectedApplicationId) {
            const applicationId = selectedApplicationId.toString();
            dispatch(loadDocs(applicationId));
            fetchTickets(applicationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedApplicationId]);

    const handleAction = async (action: string, document: ApplicationDocumentInterface) => {
        switch (action) {
            case 'DOWNLOAD': {
                handleDownload(document);
                break;
            }
            case 'DOCUMENT_UPLOAD':
            case 'REPLACE': {
                handleReplace(document);
                break;
            }
            case 'VIEW_SAMPLE': {
                setModalObject((prev) => ({
                    ...prev,
                    data: document,
                    title: 'Sample Document',
                    action: 'VIEW_SAMPLE',
                    disabled: false,
                    content: '',
                }));
                break;
            }
            case 'VIEW_TEMPLATE': {
                // "Template from URL" docs have no uploaded file — open the linked template directly.
                if (!document.template_uri) {
                    const templateUrl = getExplanationUrl(document, user.language_setting);
                    if (templateUrl) {
                        window.open(templateUrl, '_blank', 'noopener,noreferrer');
                        break;
                    }
                }
                setModalObject((prev) => ({
                    ...prev,
                    data: document,
                    title: 'Template Document',
                    action: 'VIEW_TEMPLATE',
                    disabled: false,
                    content: '',
                }));
                break;
            }
            case 'DOCUMENT_PREVIEW': {
                setModalObject((prev) => ({
                    ...prev,
                    data: document,
                    title: 'Document Preview',
                    action: 'DOCUMENT_PREVIEW',
                    disabled: false,
                    content: '',
                }));
                break;
            }
            case 'DELETE': {
                setModalObject((prev) => ({
                    ...prev,
                    data: document,
                    title: 'Delete Document',
                    content: `Are you sure you want to delete "${document.document_name}"? This will remove the uploaded file.`,
                    action: 'DELETE',
                    disabled: false,
                }));
                break;
            }
            default:
                break;
        }
    };

    const handleDownload = async (document: ApplicationDocumentInterface, isDownload = true) => {
        try {
            if (!document.resource_name) {
                dispatch(
                    setAlert({
                        message: `Document resource not available for download or preview`,
                        type: 'error',
                    })
                );
                return;
            }
            handleDownloadDoc(selectedApplicationId.toString(), document);
            if (isDownload) {
                dispatch(setAlert({ message: `Document download started`, type: 'error' }));
            }
        } catch {
            dispatch(setAlert({ message: `Failed to download document`, type: 'error' }));
        }
    };

    const handleReplace = (document: ApplicationDocumentInterface) => {
        if (!canUploadDocuments) {
            dispatch(
                setAlert({
                    message: `Cannot replace documents when application is in this status`,
                    type: 'error',
                })
            );
            return;
        }

        setModalObject((prev) => ({
            ...prev,
            data: document,
            title: 'Replace Document',
            action: 'DOCUMENT_UPLOAD',
            disabled: false,
            content: '',
        }));
    };

    // Check if user can upload documents based on application status
    const canUploadDocuments = selectedApplication
        ? !['DOCUMENTS_IN_PROCESS', 'SUBMITTED', 'APPROVED', 'REJECTED', 'CLOSED'].includes(
              selectedApplication.application_status
          )
        : false;

    const handleUpload = async (file: File) => {
        if (!selectedApplication || !modalObject.data) return;

        const doc = modalObject.data as ApplicationDocumentInterface;
        const success = await handleUploadDoc(file, doc, selectedApplicationId.toString());

        if (!success) return;

        dispatch(setAlert({ message: 'Document uploaded successfully', type: 'success' }));

        const openTicket = tickets?.find((t) => t.document_id === doc.id);
        if (openTicket && openTicket?.status !== 'RESOLVED') {
            setModalObject((p) => ({
                ...p,
                data: openTicket,
                title: 'Confirm Ticket Resolution',
                content:
                    'Replacing the document will resolve the associated ticket. Do you want to proceed?',
                action: 'CONFIRM',
            }));
        } else {
            onResetModalObj();
        }
    };

    const handleConfirm = async () => {
        if (modalObject.action === 'CONFIRM' && selectedApplicationId && modalObject.data) {
            const ticketId = (modalObject.data as TicketInterface).id;
            const success = await resolveTicket(
                ticketId,
                'RESOLVED',
                selectedApplicationId.toString()
            );
            if (success) {
                onResetModalObj();
                dispatch(setAlert({ message: 'Ticket marked as resolved', type: 'success' }));
            }
            return;
        }

        if (modalObject.action === 'DELETE' && selectedApplicationId && modalObject.data) {
            try {
                setModalObject((prev) => ({ ...prev, disabled: true }));
                const doc = modalObject.data as ApplicationDocumentInterface;
                // deleteDocs expects AppDocument[]; runtime shape is compatible for delete API
                await dispatch(deleteDocs([doc as never]));
                await dispatch(loadDocs(selectedApplicationId.toString()));
                dispatch(setAlert({ message: 'Document deleted', type: 'success' }));
                onResetModalObj();
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, 'Failed to delete document'),
                        type: 'error',
                    })
                );
                setModalObject((prev) => ({ ...prev, disabled: false }));
            }
        }
    };

    const handleToggleResolve = async (ticket: TicketInterface, isResolved: boolean) => {
        try {
            if (!selectedApplicationId) {
                dispatch(setAlert({ message: 'No application selected', type: 'error' }));
                return;
            }

            const matchedDocument = documents.find((doc) => doc.id === ticket.document_id);

            if (!matchedDocument) {
                dispatch(
                    setAlert({
                        message: 'Associated document not found for this ticket',
                        type: 'error',
                    })
                );
                return;
            }

            const isDocumentNotUploaded = matchedDocument.status === AppDocStatus.FIX_REQUIRED;

            if (isDocumentNotUploaded) {
                handleAction(
                    'DOCUMENT_UPLOAD',
                    matchedDocument as unknown as ApplicationDocumentInterface
                );
            } else {
                setResolvingIds((prev) => new Set(prev).add(ticket.id));

                const newStatus = isResolved ? 'PENDING' : 'RESOLVED';
                const success = await resolveTicket(
                    ticket.id,
                    newStatus,
                    selectedApplicationId.toString()
                );
                if (success) {
                    dispatch(
                        setAlert({
                            message: `Ticket for document "${matchedDocument.document_name}" marked as ${isResolved ? 'pending' : 'resolved'}`,
                            type: 'success',
                        })
                    );
                }
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to update ticket status';
            dispatch(setAlert({ message: errorMessage, type: 'error' }));
        } finally {
            setResolvingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(ticket.id);
                return newSet;
            });
        }
    };

    // Filter documents by status and search query
    const filteredDocuments = documents.filter((doc) => {
        const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
        const matchesSearch =
            searchQuery === '' ||
            doc.document_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.explanation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.resource_name?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    // Count documents by status
    const statusCounts = documents.reduce(
        (acc, doc) => {
            acc[doc.status] = (acc[doc.status] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );

    if (isLoadingApplications) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <Loader />
            </Box>
        );
    }

    if (applicationsError) {
        return (
            <Box sx={{ p: 3 }}>
                <Result
                    title="Error"
                    description="Failed to load applications. Please try again later."
                />
            </Box>
        );
    }

    if (!applications || applications.length === 0) {
        return <NoApplicationScreen />;
    }

    return (
        <PageBackground rootSx={{ overflow: 'auto' }}>
            {/* <PageHeader title="Documents" description="Manage your immigration documents" /> */}
            <PageHeader
                title="Documents"
                description="Please note: Any document, that is not a government form or passport, that is not in an official language of the target country must be accompanied by a certified translation and a translator's affidavit."
            />

            {/* Tickets Panel */}
            {/* <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <TicketsPanel
          tickets={tickets}
          isLoading={false}
          applicationId={selectedApplicationId || undefined}
        />
      </Box> */}

            {/* Tickets Alert - Inline display */}
            <TicketsAlert
                tickets={tickets}
                resolvingIds={resolvingIds}
                handleToggleResolve={handleToggleResolve}
            />

            {/* Upload restriction notice */}
            {selectedApplication && !canUploadDocuments && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Document upload and replacement are disabled because the application status is{' '}
                    <strong>{selectedApplication.application_status}</strong>. You can only view and
                    download documents.
                </Alert>
            )}

            {/* No application selected state */}
            {!selectedApplicationId ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        bgcolor: 'background.default',
                        borderRadius: 2,
                    }}
                >
                    <Upload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Select an Application
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose an application from the dropdown above to view and manage its
                        documents
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* Filter Tabs and View Toggle */}
                    <Box
                        sx={{
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            // borderBottom: 1,
                            borderColor: 'divider',
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                        }}
                    >
                        <Tabs
                            value={filterStatus}
                            onChange={(_, value) => setFilterStatus(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                flexGrow: { xs: 0, sm: 1 },
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: '100%',
                            }}
                        >
                            <Tab
                                disableRipple
                                label={`All (${documents.length})`}
                                value="all"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    minWidth: 'auto',
                                }}
                            />
                            {Object.values(DocumentStatus).map((status) => {
                                const count = statusCounts[status] || 0;
                                if (count === 0) return null;

                                return (
                                    <Tab
                                        disableRipple
                                        key={status}
                                        label={`${toTitleCase(status)} (${count})`}
                                        value={status}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            minWidth: 'auto',
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {/* Search Bar */}
                        <Stack
                            direction="row"
                            sx={{ alignItems: 'center', width: { xs: '100%', md: 'auto' } }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search documents"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ width: { xs: '100%', sm: 300 } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchQuery && (
                                            <InputAdornment position="end">
                                                <Tooltip title="Clear search">
                                                    <Button
                                                        size="small"
                                                        onClick={() => setSearchQuery('')}
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        <Clear fontSize="small" />
                                                    </Button>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />

                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_, value) => value && setViewMode(value)}
                                size="small"
                                sx={{ ml: 2, mb: 1, display: { xs: 'none', sm: 'flex' } }}
                            >
                                <ToggleButton disableRipple value="grid" aria-label="grid view">
                                    <Tooltip title="Grid View" placement="top">
                                        <ViewModule fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton disableRipple value="row" aria-label="row view">
                                    <Tooltip title="List View" placement="top">
                                        <ViewList fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Box>

                    {/* Documents Grid */}
                    {isLoadingDocuments ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 8,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : documentsError ? (
                        <Result
                            icon={<ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />}
                            title="Failed to load documents"
                            description="Please try again later."
                        />
                    ) : filteredDocuments.length === 0 ? (
                        <Result
                            icon={<Upload sx={{ fontSize: 64, color: 'text.secondary' }} />}
                            title={
                                searchQuery
                                    ? 'No documents found'
                                    : filterStatus === 'all'
                                      ? 'No documents yet'
                                      : `No ${filterStatus.toLowerCase()} documents`
                            }
                            description={
                                searchQuery
                                    ? `No documents match "${searchQuery}". Try a different search term.`
                                    : filterStatus === 'all'
                                      ? 'No documents available for this application'
                                      : `You don't have any ${filterStatus.toLowerCase()} documents`
                            }
                        >
                            {searchQuery && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Clear />}
                                    onClick={() => setSearchQuery('')}
                                >
                                    Clear Search
                                </Button>
                            )}
                        </Result>
                    ) : viewMode === 'grid' ? (
                        <Grid container spacing={3}>
                            {filteredDocuments.map((document) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={document.id}>
                                    <DocumentCard
                                        viewMode="grid"
                                        document={document}
                                        isDownloading={isDownloading}
                                        canUpload={canUploadDocuments}
                                        handleAction={handleAction}
                                        getTemplateUrl={(doc) =>
                                            getExplanationUrl(doc, user.language_setting)
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Stack spacing={2}>
                            {filteredDocuments.map((document) => (
                                <DocumentCard
                                    viewMode="row"
                                    key={document.id}
                                    document={document}
                                    isDownloading={isDownloading}
                                    canUpload={canUploadDocuments}
                                    handleAction={handleAction}
                                    getTemplateUrl={(doc) =>
                                        getExplanationUrl(doc, user.language_setting)
                                    }
                                />
                            ))}
                        </Stack>
                    )}
                </>
            )}

            {/* Upload Dialog */}
            {modalObject.action === 'DOCUMENT_UPLOAD' && (
                <DocumentUploadDialog
                    open
                    onClose={onResetModalObj}
                    onUpload={handleUpload}
                    document={modalObject.data as ApplicationDocumentInterface}
                    isUploading={isUploading}
                    tickets={tickets}
                    handleAction={handleAction}
                />
            )}

            {modalObject.action === 'DOCUMENT_PREVIEW' && modalObject.data && (
                <DocumentPreviewModal
                    documentId={(modalObject.data as ApplicationDocumentInterface).id}
                    documentName={(modalObject.data as ApplicationDocumentInterface).document_name}
                    mobile={isMobile}
                    onClose={onResetModalObj}
                />
            )}

            {modalObject.action === 'VIEW_TEMPLATE' &&
                selectedApplicationId &&
                modalObject.data && (
                    <TemplatePreviewModal
                        applicationId={String(selectedApplicationId)}
                        documentId={(modalObject.data as ApplicationDocumentInterface).id}
                        documentName={
                            (modalObject.data as ApplicationDocumentInterface).document_name
                        }
                        mobile={isMobile}
                        onClose={onResetModalObj}
                    />
                )}

            {modalObject.action === 'VIEW_SAMPLE' && selectedApplicationId && modalObject.data && (
                <SamplePreviewModal
                    applicationId={String(selectedApplicationId)}
                    documentId={(modalObject.data as ApplicationDocumentInterface).id}
                    documentName={(modalObject.data as ApplicationDocumentInterface).document_name}
                    mobile={isMobile}
                    onClose={onResetModalObj}
                />
            )}

            {(modalObject.action === 'CONFIRM' || modalObject.action === 'DELETE') && (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    onConfirm={handleConfirm}
                    onClose={onResetModalObj}
                    loading={modalObject.disabled}
                />
            )}
        </PageBackground>
    );
};

export default DocumentsPage;
